/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { TextField, CircularProgress ,Switch, FormControlLabel} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

import Page from '../../../components/Page';
import subscriptionServices from '../../../services/subscriptionServices';
import adminServices from '../../../services/adminServices';
import ErrorFocus from '../../../components/ErrorFocus/ErrorFocus';
import SnackBar from '../../../components/snackbar/snackbar';

import { SectionCard, FieldLabel, BackLink, PageHeading, SearchBox, TierButton } from './components/shared/SharedComponents';

const BillingToggle = ({ value, onChange }) => (
  <div className="flex border border-gray-200 rounded-[7px] overflow-hidden">
    {['Yearly', 'Monthly'].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium cursor-pointer select-none transition-all"
        style={{
          background: value === opt ? '#eff6ff' : '#fff',
          color: value === opt ? '#1d4ed8' : '#374151',
          border: value === opt ? '1.5px solid #2563eb' : '1px solid transparent',
          borderRadius: value === opt ? '6px' : 0,
        }}
      >
        {opt}
      </button>
    ))}
  </div>
);

const CreateSingleCoursePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [submittingPublish, setSubmittingPublish] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, title: '' });
  const publishModeRef = React.useRef('draft');
  const [usedCourseIds, setUsedCourseIds] = useState([]);
  const [pricingMode, setPricingMode] = useState('Paid');
  const fromTab = location.state?.fromTab ?? 1;

  const [lookups, setLookups] = useState({ subscription_ids: [], currency_type: [] });

  useEffect(() => { fetchCourses(); fetchLookups(); fetchUsedCourseIds(); }, []);

  const fetchUsedCourseIds = async () => {
    try {
      const res = await subscriptionServices.getSubscriptionPlans({ planCategory: 'single', pageSize: 1000 });
      if (res.ok) {
        const ids = (res.data?.rows || [])
          .flatMap(plan => (plan.courseDetails || []).map(c => c.id))
          .filter(Boolean);
        setUsedCourseIds(ids);
      }
    } catch (e) { console.error('[CreateSingleCoursePlan usedCourseIds]', e); }
  };

  const fetchLookups = async () => {
    try {
      const res = await adminServices.getLookUpData('subscription_ids,currency_type');
      console.log('lookup result:', res.ok, res.data);
      if (res.ok) setLookups(res.data || {});
    } catch (e) { console.error('[CreateSingleCoursePlan lookups]', e); }
  };

  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      const res = await adminServices.getLookUpData(['course']);
      if (res.ok) setCourses((res.data.course || []).filter((c) => Number(c.status) === 1));
    } catch (e) { console.error('[CreateSingleCoursePlan]', e); }
    setCoursesLoading(false);
  };

  const filteredCourses = courses.filter((c) => {
    const code = (c.audienceCode || '').toUpperCase();
    const freeOk = pricingMode === 'Free' ? c.isFree === true : !c.isFree;
    return freeOk
      && (code === 'B2C' || code === 'BOTH')
      // && !usedCourseIds.includes(c.id)
      && (c.title || '').toLowerCase().includes(courseSearch.toLowerCase());
  });

  console.log(courses,"coursescourses");
  

  const initialValues = {
    planName: '', displayName: '', description: '',enableonline:false,
    courseId: null, courseTitle: '', priceINR: '', priceUSD: '', stripePriceId: '',
    billingCycle: 'Monthly', pricingMode: 'Paid', billingtype: 'recurring'
  };
  const validationSchema = Yup.object().shape({
    planName: Yup.string().trim().min(3, 'Minimum 3 characters').required('Required'),
    courseId: Yup.number().nullable().required('Select a course'),
    priceINR: Yup.number().min(0, 'Must be ≥ 0').when('pricingMode', {
      is: 'Paid',
      then: (s) => s.required('Required'),
      otherwise: (s) => s.notRequired(),
    }),
    priceUSD: Yup.number().min(0, 'Must be ≥ 0').when('pricingMode', {
      is: 'Paid',
      then: (s) => s.required('Required'),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const handleSubmit = async (values, publishMode = 'draft') => {
    if (submittingDraft || submittingPublish) return;
    if (publishMode === 'draft') setSubmittingDraft(true);
    else setSubmittingPublish(true);
    try {
       if (publishMode === 'publish' && !values.enableonline) {
          setSnackbar({
            open: true,
            title: 'Please enable online before publishing.',
          });
          setSubmittingPublish(false)
          return false;
        }

      const fd = new FormData();
      fd.append('name', values.planName);
      fd.append('shortDescription', JSON.stringify(values.displayName ? [values.displayName] : []));
      fd.append('description', values.description || '');
      fd.append('is_active', values.enableonline)
      fd.append('planType', 'single');
      fd.append('plan_category', 'SINGLE_COURSE_PLAN');
      fd.append('scope', 'platform');
      const subName = values.billingCycle === 'Yearly' ? 'Online Yearly' : 'Online Monthly';
      fd.append('subscriptionPlan', subName);
      fd.append('subscriptionType', 'B2C');
      fd.append('isOnline', publishMode === 'publish' ? 'true' : 'false');
      fd.append('billingtype', values.billingtype);
      const isFree = values.pricingMode === 'Free';
      fd.append('isPaid', isFree ? 'false' : 'true');
      fd.append('frequency', values.billingCycle === 'Yearly' ? '1' : '2');
      fd.append('courseIds', JSON.stringify([values.courseId]));
      fd.append('stripePriceId', values.stripePriceId || '');
      const currencies = (lookups.currency_type || []).length > 0
        ? lookups.currency_type
        : [{ code: 'INR' }, { code: 'USD' }];

      const getPrice = (code) => code === 'INR' ? Number(values.priceINR) || 0 : Number(values.priceUSD) || 0;
      fd.append('prices', JSON.stringify(
        currencies.map(c => ({
          currencyType: c.code,
          actualCost: isFree ? 0 : getPrice(c.code),
        }))
      ));
      const subIds = lookups.subscription_ids || [];
      const subId = subIds.find(s => s.name === subName)?.id;
      if (!subId) {
        setSnackbar({ open: true, title: `Subscription record "${subName}" not found. Contact admin.` });
        if (publishMode === 'draft') setSubmittingDraft(false); else setSubmittingPublish(false);
        return;
      }
      const res = await subscriptionServices.postSubscriptionPlan(subId, fd, 'single');
      if (res.ok) {
        navigate('/app/subscription?tab=1', { state: { message: publishMode === 'publish' ? 'Plan published!' : 'Draft saved' } });
      } else {
        setSnackbar({ open: true, title: res.data?.message || 'Something went wrong' });
      }
    } catch (e) {
      console.error('[CreateSingleCoursePlan]', e);
      setSnackbar({ open: true, title: 'Something went wrong' });
    }
    if (publishMode === 'draft') setSubmittingDraft(false);
    else setSubmittingPublish(false);
  };

  const renderCourseList = (values, setFieldValue) => {
    if (coursesLoading) {
      return (
        <div className="flex justify-center py-8">
          <CircularProgress size={20} sx={{ color: '#2563eb' }} />
        </div>
      );
    }
    if (filteredCourses.length === 0) {
      return (
        <div className="flex justify-center py-8">
          <p className="text-[13px] text-gray-400">No courses available</p>
        </div>
      );
    }
    return filteredCourses.map((c, idx) => {
      const selected = values.courseId === c.id;
      return (
        <button
          key={c.id}
          type="button"
          onClick={() => { setFieldValue('courseId', c.id); setFieldValue('courseTitle', c.title); }}
          className="flex items-center w-full text-left px-[14px] py-3 cursor-pointer hover:bg-gray-50 bg-transparent border-none"
          style={{ borderBottom: idx < filteredCourses.length - 1 ? '1px solid #f3f4f6' : 'none' }}
        >
          {selected
            ? <RadioButtonCheckedIcon sx={{ fontSize: '18px', color: '#2563eb', mr: '10px', flexShrink: 0 }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: '18px', color: '#d1d5db', mr: '10px', flexShrink: 0 }} />}
          <div>
            <p className="text-[13px] font-medium text-gray-900">{c.title}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-[0.04em]">{c.type || c.courseType || '—'}</p>
          </div>
        </button>
      );
    });
  };

  return (
    <Page title="">
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6">
        <BackLink label="Back to Plans" onClick={() => navigate(`/app/subscription?tab=${fromTab}`)} />
        <PageHeading title="Create Single Course Plan" subtitle="Create a plan for individual access to a single B2C course." />

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(v) => handleSubmit(v, publishModeRef.current)}>
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <ErrorFocus />

              {/* 1 — Plan Identity */}
              <SectionCard step={1} title="Plan Identity" subtitle="Name for this single-course plan.">
                <div className="flex flex-col gap-[18px]">
                  <div>
                    <FieldLabel required>Plan name (internal)</FieldLabel>
                    <TextField fullWidth name="planName" placeholder="e.g. HIPAA Compliance — Individual" value={values.planName} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.planName && errors.planName)} helperText={touched.planName && errors.planName} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Display name (customer-facing)</FieldLabel>
                    <TextField fullWidth name="displayName" placeholder="e.g. HIPAA Compliance" value={values.displayName} onChange={handleChange} onBlur={handleBlur} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Description</FieldLabel>
                    <TextField fullWidth multiline rows={3} name="description" placeholder="e.g. Individual access to this course with full content" value={values.description} onChange={handleChange} onBlur={handleBlur} size="small" />
                  </div>
                  <div>
                    <FieldLabel>Enable Online</FieldLabel>
                    <FormControlLabel
                      control={<Switch name ='enableonline' checked={values.enableonline} onChange={(e) => setFieldValue('enableonline', e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#2563eb' } }} />}
                      label={<span className="text-[13px] text-gray-700">Mark as Active</span>}
                    />
                  </div>
                </div>
              </SectionCard>
            {/* 2 — Pricing */}
              <SectionCard step={2} title="Pricing" subtitle="Set the pricing mode and price details for this individual course plan.">
                {/* Paid / Free toggle */}
                <div className="flex border border-gray-200 rounded-[7px] overflow-hidden w-fit mb-4">
                  {['Paid', 'Free'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => { setFieldValue('pricingMode', opt); setPricingMode(opt); }}
                      className="px-5 py-[9px] text-[13px] font-medium cursor-pointer select-none transition-all"
                      style={{
                        background: values.pricingMode === opt ? '#1d4ed8' : '#fff',
                        color: values.pricingMode === opt ? '#fff' : '#374151',
                        border: values.pricingMode === opt ? '1.5px solid #1d4ed8' : '1px solid transparent',
                        borderRadius: values.pricingMode === opt ? '6px' : 0,
                      }}
                    >
                      {opt === 'Paid' ? 'Paid Plan' : 'Free Plan'}
                    </button>
                  ))}
                </div>

                {values.pricingMode === 'Free' ? (
                  <div className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3">
                    <p className="text-[13px] font-semibold text-green-800">Free access — no payment required</p>
                    <p className="text-[12px] text-green-700 mt-[2px]">
                      Prices will be set to ₹0 / $0. No Stripe product will be linked. The course will be individually accessible at no charge.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1">
                        <FieldLabel required>Price (₹/{values.billingCycle === 'Yearly' ? 'year' : 'month'})</FieldLabel>
                        <TextField fullWidth name="priceINR" type="number" placeholder="999" value={values.priceINR} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.priceINR && errors.priceINR)} helperText={touched.priceINR && errors.priceINR}
                          InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">₹</span> }} size="small" />
                      </div>
                      <div className="flex-1">
                        <FieldLabel required>Price (USD/{values.billingCycle === 'Yearly' ? 'year' : 'month'})</FieldLabel>
                        <TextField fullWidth name="priceUSD" type="number" placeholder="12.99" value={values.priceUSD} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.priceUSD && errors.priceUSD)} helperText={touched.priceUSD && errors.priceUSD}
                          InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">$</span> }} size="small" />
                      </div>
                    </div>
                    <div className="flex-1 mb-4">
                      <FieldLabel>Billing cycle</FieldLabel>
                      <BillingToggle value={values.billingCycle} onChange={(v) => setFieldValue('billingCycle', v)} />
                    </div>
                    <div>
                      <FieldLabel>Stripe Price ID</FieldLabel>
                      <TextField fullWidth name="stripePriceId" InputProps={{ readOnly: true }} placeholder="price_annual_ ..." value={values.stripePriceId} onChange={handleChange} onBlur={handleBlur} size="small" />
                      <p className="text-[11px] text-gray-400 mt-1">Auto-synced to the course record on Publish.</p>
                    </div>

                    <div className="mt-4">
                      <FieldLabel required>Billing Type</FieldLabel>
                      <div className="flex gap-[10px]">
                        {['recurring', 'one_time'].map((t) => (
                          <TierButton
                            key={t}
                            label={t}
                            selected={values.billingtype === t}
                            onClick={() => setFieldValue('billingtype', t)}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}


              </SectionCard>
              {/* 3 — Course */}
              <SectionCard step={3} title="Course" subtitle="Select the single B2C course this plan provides access to.">
                <div className="mb-2">
                  <SearchBox value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Search courses..." />
                </div>
                {touched.courseId && errors.courseId && (
                  <p className="text-[11px] text-red-500 mb-[6px]">{errors.courseId}</p>
                )}
                <div className="border border-gray-200 rounded-[7px] max-h-[240px] overflow-y-auto">
                  {renderCourseList(values, setFieldValue)}
                </div>
              </SectionCard>

  

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 pb-10">
                <button type="button" onClick={() => navigate('/app/subscription?tab=1')}
                  className="text-[13px] font-semibold text-[#374151] cursor-pointer px-5 py-[6px] rounded-[8px] border border-[#d1d5db] bg-white hover:border-[#9ca3af] hover:bg-[#f9fafb] transition-all duration-150">
                  Cancel
                </button>
                <div className="flex gap-[10px]">
                  <LoadingButton
                    type="submit"
                    loading={submittingDraft}
                    variant="outlined"
                    onClick={() => { publishModeRef.current = 'draft'; }}
                    sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', borderColor: '#d1d5db', color: '#374151', textTransform: 'none', px: '20px' }}>
                    Save as Draft
                  </LoadingButton>
                  {/* <LoadingButton
                    type="submit"
                    loading={submittingPublish}
                    variant="contained"
                    onClick={() => { publishModeRef.current = 'publish'; }}
                    sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', background: '#2563eb', textTransform: 'none', px: '20px', '&:hover': { background: '#1d4ed8' } }}>
                    Publish Plan
                  </LoadingButton> */}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <SnackBar open={snackbar.open} snackbarTitle={snackbar.title} close={() => setSnackbar({ open: false, title: '' })} />
    </Page>
  );
};

export default CreateSingleCoursePlan;